// ═══════════════════════════════════════════════════════════════════════════════
// Hub-Native Analyst Engineering Request — Submission Modal
//
// Form fields per spec §5.2. Markdown description; multipart attachments
// remain unsupported in P1 (§9 #9) — the form surfaces a clear "attachments
// not yet supported in P1" message so analysts know to paste hashes/links
// instead.
// ═══════════════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { theme } from '../../lib/theme';
import { Button, Card } from '../shared';
import { useApp } from '../../hooks/useAppState';
import { api } from '../../services/api';

interface Props {
  referringRoute: string;
  /** When opened from incident detail, prefilled here. */
  relatedIncidentId?: string;
  /** When opened from detection / threat-hunting detail, prefilled here. */
  relatedDetectionId?: string;
  onClose: () => void;
  onSubmitted?: (requestId: string) => void;
}

type Category = 'detection' | 'automation' | 'tuning' | 'other';
type Priority = 'low' | 'normal' | 'high' | 'urgent';

const CATEGORY_OPTIONS: Array<{ id: Category; label: string }> = [
  { id: 'detection',  label: 'Detection (new rule / analytic)' },
  { id: 'automation', label: 'Automation (playbook / response action)' },
  { id: 'tuning',     label: 'Tuning (false-positive reduction / threshold)' },
  { id: 'other',      label: 'Other' },
];

const PRIORITY_OPTIONS: Array<{ id: Priority; label: string }> = [
  { id: 'low',    label: 'Low' },
  { id: 'normal', label: 'Normal' },
  { id: 'high',   label: 'High' },
  { id: 'urgent', label: 'Urgent' },
];

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 6,
  border: `1px solid ${theme.border}`,
  background: theme.input,
  color: theme.text,
  fontFamily: 'inherit',
  fontSize: 12,
  boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = {
  fontSize: 10,
  color: theme.textTertiary,
  display: 'block',
  marginBottom: 4,
  fontWeight: 600,
  letterSpacing: 0.5,
};

export function AnalystRequestModal({
  referringRoute,
  relatedIncidentId,
  relatedDetectionId,
  onClose,
  onSubmitted,
}: Props) {
  const { toast, navigate } = useApp();

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<Category>('detection');
  const [priority, setPriority] = useState<Priority>('normal');
  const [description, setDescription] = useState('');
  const [incidentId, setIncidentId] = useState(relatedIncidentId || '');
  const [detectionId, setDetectionId] = useState(relatedDetectionId || '');
  const [desiredBy, setDesiredBy] = useState('');
  const [ack, setAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const titleInvalid = !title.trim() || title.trim().length > 120;
  const descInvalid = !description.trim();
  const formValid = !titleInvalid && !descInvalid && ack && !submitting;

  async function submit() {
    if (!formValid) return;
    setSubmitting(true);
    setError(null);
    try {
      const submissionContext: NonNullable<Parameters<typeof api.engineeringRequests.analystSubmit>[0]['submissionContext']> = {};
      if (referringRoute) submissionContext.referringRoute = referringRoute;
      if (incidentId.trim()) submissionContext.relatedIncidentId = incidentId.trim();
      if (detectionId.trim()) submissionContext.relatedDetectionId = detectionId.trim();

      const result = await api.engineeringRequests.analystSubmit({
        title: title.trim(),
        category,
        priority,
        description: description.trim(),
        desiredBy: desiredBy || null,
        acknowledgement: true,
        submissionContext: Object.keys(submissionContext).length ? submissionContext : undefined,
      });

      const fsBit = result.freshserviceTicketId
        ? ` · FS #${result.freshserviceTicketId}`
        : (result.freshserviceError ? ' · FS pending' : '');
      toast(`Engineering request submitted (${result.requestId})${fsBit}`);
      onSubmitted?.(result.requestId);
      // Navigate to My Requests so analyst sees the submission immediately.
      navigate('my-engineering-requests');
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Submission failed');
      setSubmitting(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 95,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 'min(640px, 100%)',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <Card padding={20} style={{ borderLeft: `4px solid ${theme.accent}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: theme.text }}>
                🔧 Request engineering help
              </div>
              <div style={{ fontSize: 11, color: theme.textTertiary, marginTop: 2 }}>
                MXDR Engineering will pick this up from the queue. Track progress in My Engineering Requests.
              </div>
            </div>
            <Button sm onClick={onClose}>✕</Button>
          </div>

          {error && (
            <div style={{
              padding: 10, marginBottom: 12, borderRadius: 6,
              background: `${theme.red}15`, border: `1px solid ${theme.red}55`,
              color: theme.text, fontSize: 12,
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Title (required, ≤120 chars)</label>
              <input
                style={inputStyle}
                value={title}
                onChange={e => setTitle(e.target.value)}
                maxLength={120}
                placeholder="Short summary — e.g. 'New detection: anomalous OAuth grant on GovCloud tenants'"
              />
              <div style={{ fontSize: 9, color: titleInvalid && title.length > 0 ? theme.red : theme.textTertiary, marginTop: 2 }}>
                {title.length}/120
              </div>
            </div>

            <div>
              <label style={labelStyle}>Category (required)</label>
              <select
                style={inputStyle}
                value={category}
                onChange={e => setCategory(e.target.value as Category)}
              >
                {CATEGORY_OPTIONS.map(c => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Priority (required)</label>
              <select
                style={inputStyle}
                value={priority}
                onChange={e => setPriority(e.target.value as Priority)}
              >
                {PRIORITY_OPTIONS.map(p => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Description (required, markdown supported)</label>
              <textarea
                style={{ ...inputStyle, minHeight: 140, fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder={'What do you need engineering to build / change?\n\n- Problem:\n- Customers affected:\n- What you already tried:\n- Acceptance:'}
              />
            </div>

            <div>
              <label style={labelStyle}>Related incident (optional)</label>
              <input
                style={inputStyle}
                value={incidentId}
                onChange={e => setIncidentId(e.target.value)}
                placeholder="incident id, e.g. ir-12345"
              />
            </div>

            <div>
              <label style={labelStyle}>Related detection (optional)</label>
              <input
                style={inputStyle}
                value={detectionId}
                onChange={e => setDetectionId(e.target.value)}
                placeholder="detection id / saved-search name"
              />
            </div>

            <div>
              <label style={labelStyle}>Desired-by date (optional)</label>
              <input
                style={inputStyle}
                type="date"
                value={desiredBy}
                onChange={e => setDesiredBy(e.target.value)}
              />
            </div>

            <div style={{ gridColumn: '1 / -1', fontSize: 11, color: theme.textTertiary, padding: '8px 10px', background: theme.input, borderRadius: 6, border: `1px dashed ${theme.border}` }}>
              <strong>Attachments:</strong> not supported in P1.
              Paste links, hashes, or screenshot URLs into the description.
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: theme.text, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={ack}
                  onChange={e => setAck(e.target.checked)}
                />
                <span>I have searched existing requests / runbooks. (required)</span>
              </label>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <Button onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button primary onClick={submit} disabled={!formValid}>
              {submitting ? 'Submitting…' : 'Submit request'}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
